import { languageMap } from "screens/patientInfo/helpers/language";
import { constants } from "../constants/redux-constants";
import { AVAILABLE_LANGUAGES } from "config/helpers/multilingual_helper";
import { cdssCustomTitleValidator } from "config/helpers/common_helper";

const initialState = {
  token: null,
  recordingByEncounter: {},
  isEnding: false,
  language: AVAILABLE_LANGUAGES?.[0],
  hasErrorEncountered: false,
  lastSyncedTimeStamp: null,
  cdss: null,
  cdssTabProps: {
    isSet: false,
    title: null,
    color: null,
  },
  loading: {
    recording: false,
    paused: false,
  },
  progressState: {},
  autoStart: false,
  isRecordingInProgress: false,
  endLoading: false,
  zoomCallOngoing: false,
};

const recognizerReducer = (state = initialState, action) => {
  switch (action.type) {
    case constants?.RECOGNIZER?.LANGUAGE:
      return {
        ...state,
        language: action?.payload || state?.language,
      };
    case constants?.RECOGNIZER?.SET_STT_TOKEN:
      return {
        ...state,
        token: action?.payload,
        hasErrorEncountered: false,
        isEnding: false,
      };

    case constants.RECOGNIZER.START_RECORDING: {
      const { patientId, encounterId, conversationId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            recording: true,
            paused: false,
            hasErrorEncountered: false,
            conversationId,
          },
        },
      };
    }
    case constants.RECOGNIZER.START_RECORDING_LOADING:
      return {
        ...state,
        loading: {
          ...state?.loading,
          recording: action.payload,
        },
      };

    case constants.RECOGNIZER.PAUSE_RECORDING: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            recording: false,
            paused: true,
          },
        },
      };
    }

    case constants.RECOGNIZER.RESUME_RECORDING: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            recording: true,
            paused: false,
          },
        },
      };
    }

    case constants.RECOGNIZER.STOP_RECORDING: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      const { [key]: removed, ...rest } = state.recordingByEncounter;
      return {
        ...state,
        recordingByEncounter: rest,
      };
    }

    case constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED: {
      const { patientId, encounterId, error } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        hasErrorEncountered: error,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            recording: true,
            paused: false,
          },
        },
      };
    }

    case constants.RECOGNIZER.CONVERSATION_ENDING:
      return {
        ...state,
        isEnding: true,
      };

    case constants.RECOGNIZER.UPDATE_CDSS_DATA:
      if (action.payload?.lastComputedAt !== state.lastSyncedTimeStamp) {
        return {
          ...state,
          lastSyncedTimeStamp: action.payload?.lastComputedAt,
          cdss: action.payload?.result || {},
        };
      }

      return state;
    case constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER:
      return {
        ...state,
        cdss: null,
        token: null,
        lastSyncedTimeStamp: null,
        cdssTabProps: {
          isSet: false,
          title: null,
          color: null,
        },
        // keep per-encounter states as is
        recordingByEncounter: state.recordingByEncounter,
        progressState: state.progressState,
      };

    case constants.RECOGNIZER.AUTO_START:
      return {
        ...state,
        autoStart: action.payload,
      };

    case constants.RECOGNIZER.START_RECORDING_IN_PROGRESS:
      return {
        ...state,
        isRecordingInProgress: action.payload,
      };

    case constants.RECOGNIZER.SET_END_LOADING:
      return {
        ...state,
        endLoading: action.payload,
      };

    case constants.RECOGNIZER.SET_CDSS_TITLE:
      const { color, isValid } = cdssCustomTitleValidator(action.payload);
      return {
        ...state,
        cdssTabProps: {
          isSet: isValid,
          title: isValid ? action.payload : null,
          color: isValid ? color : null,
        },
      };

    case constants.RECOGNIZER.UNSET_CDSS_TITLE:
      return {
        ...state,
        cdssTabProps: {
          isSet: false,
          title: null,
          color: null,
        },
      };

    case constants.RECOGNIZER.UPDATE_PROGRESS_STATE: {
      const { patientId, encounterId, ...progressData } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        progressState: {
          ...state.progressState,
          [key]: {
            patientId,
            encounterId,
            isProgressReady: progressData.isProgressReady ?? false,
            ...progressData,
          },
        },
      };
    }

    case constants.RECOGNIZER.ZOOM_CALL_ONGOING:
      return {
        ...state,
        zoomCallOngoing: action.payload,
      };

    case constants.RECOGNIZER.RESET_PROGRESS_STATE: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      const { [key]: removed, ...rest } = state.progressState;
      return {
        ...state,
        progressState: rest,
      };
    }

    case constants.RECOGNIZER.RESET_RECOGNIZER:
      return { ...initialState };

    // Dispatched on login to clear any `recording: true` entries that were
    // persisted by a previous session that ended without a clean
    // STOP_RECORDING dispatch (crash, hard navigation, network drop).
    //
    // Only removes actively-recording entries (`recording === true`).
    // Paused entries (`recording === false, paused === true`) are
    // harmless to the copilot mute gate and may still be meaningful to
    // the scribe UI, so they are preserved.
    //
    // Also resets the legacy `isRecordingInProgress` flag for the same
    // reason — it is only checked as a fallback when `recordingByEncounter`
    // is empty, but clearing it here keeps the two sources consistent.
    case constants.RECOGNIZER.CLEAR_STALE_RECORDINGS: {
      const cleaned = {};
      for (const [key, val] of Object.entries(
        state.recordingByEncounter || {},
      )) {
        if (val?.recording !== true) {
          cleaned[key] = val;
        }
      }
      return {
        ...state,
        recordingByEncounter: cleaned,
        isRecordingInProgress: false,
      };
    }

    default:
      return state;
  }
};

export default recognizerReducer;
